import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { Partner } from '../../types'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { useVersionControl } from '../../hooks/useVersionControl'
import { ConflictModal } from '../ui/ConflictModal'
import s from './shared.module.css'

export default function Partners() {
  const { organization, user } = useAuth()
  const { partners, refreshData } = useAppData()
  const orgId = organization!.id

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Partner | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState<Omit<Partner, 'id' | 'org_id'>>({ title: '', contact_person: '', email: '', phone: '', description: '', active: true, currency: 'USD' })

  const {
    saveWithVersionCheck,
    activeEditors,
    conflict,
    clearConflict
  } = useVersionControl<Partner & { version: number }>({
    tableName: 'partners',
    recordId: editing?.id || null
  })

  const openAdd = () => { setEditing(null); setForm({ title: '', contact_person: '', email: '', phone: '', description: '', active: true, currency: 'USD' }); setSaveError(''); setShowModal(true) }
  const openEdit = (p: Partner) => { setEditing(p); setForm({ title: p.title, contact_person: p.contact_person || '', email: p.email || '', phone: p.phone || '', description: p.description || '', active: p.active, currency: p.currency }); setSaveError(''); setShowModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSaving(true); setSaveError('')
    try {
      if (editing) {
        const result = await saveWithVersionCheck(
          editing as Partner & { version: number },
          form,
          `Updated partner: ${form.title}`
        )

        if (result.conflict) {
          setSaveError('Version conflict detected. Please review and resolve.')
          return
        }

        if (!result.success) {
          setSaveError('Failed to save. Please try again.')
          return
        }
      } else {
        await dataService.createPartner(orgId, form)
      }
      setShowModal(false); await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this partner?')) return
    await dataService.deletePartner(id); await refreshData()
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Partners</h2><p className={s.pageSubtitle}>{partners.length} partner{partners.length !== 1 ? 's' : ''}</p></div>
        <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Partner</button>
      </div>

      <div className={s.card}>
        <table className={s.table}>
          <thead><tr><th>Partner Name</th><th>Contact</th><th>Email</th><th>Currency</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {partners.length === 0 && <tr><td colSpan={6} className={s.emptyState}>No partners found</td></tr>}
            {partners.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.title}</td>
                <td>{p.contact_person || '—'}</td>
                <td>{p.email || '—'}</td>
                <td>{p.currency}</td>
                <td><Badge variant={p.active ? 'success' : 'neutral'}>{p.active ? 'Active' : 'Inactive'}</Badge></td>
                <td><div className={s.actions}>
                  <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(p)}><EditIcon size={14} /></button>
                  <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} title={editing ? 'Edit Partner' : 'Add Partner'} onClose={() => setShowModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="partnerForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="partnerForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          {editing && activeEditors.length > 0 && (
            <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '14px' }}>
              <strong>⚠️ Currently being edited by:</strong> {activeEditors.filter(e => e.user_id !== user?.id).map(e => e.display_name).join(', ') || 'another user'}
            </div>
          )}
          <div className={s.formGroup}><label>Partner Name *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Contact Person</label><input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} /></div>
            <div className={s.formGroup}><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div className={s.formGroup}><label>Currency</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value as any }))}>
                <option value="USD">USD</option><option value="GBP">GBP</option><option value="INR">INR</option>
              </select>
            </div>
          </div>
          <div className={s.formGroup}><label>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className={s.checkboxGroup}><input type="checkbox" id="pActive" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /><label htmlFor="pActive">Active</label></div>
        </form>
      </Modal>

      {conflict && (
        <ConflictModal
          conflict={conflict}
          onReload={async () => {
            clearConflict()
            await refreshData()
            setSaving(false)
          }}
          onOverride={async () => {
            clearConflict()
            setSaving(false)
          }}
          onCancel={() => {
            clearConflict()
            setSaving(false)
          }}
        />
      )}
    </div>
  )
}
